import { motion, type HTMLMotionProps } from "framer-motion";
import { forwardRef } from "react";
import { Loader2 } from "lucide-react";

import { haptic } from "@/lib/telegram";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

const variantClasses: Record<Variant, string> = {
  primary:
    "text-white shadow-[0_8px_32px_-8px_rgba(255,69,0,0.6)] " +
    "bg-gradient-to-br from-[#FF6B35] to-[#FF3D00] " +
    "hover:shadow-[0_10px_40px_-8px_rgba(255,69,0,0.8)] " +
    "active:scale-[0.98]",
  secondary:
    "glass text-white hover:bg-white/[0.06] active:bg-white/[0.04] " +
    "border border-white/[0.08]",
  ghost:
    "text-text-muted hover:text-white hover:bg-white/[0.04] active:bg-white/[0.02]",
  danger:
    "bg-danger/10 text-danger border border-danger/30 hover:bg-danger/15 active:bg-danger/20",
};

const sizeClasses: Record<Size, string> = {
  sm: "h-9 px-4 text-sm rounded-2xl",
  md: "h-12 px-5 text-[15px] rounded-2xl",
  lg: "h-14 px-6 text-base rounded-[20px]",
};

export interface ButtonProps extends Omit<HTMLMotionProps<"button">, "size"> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  hapticStyle?: Parameters<typeof haptic.impact>[0];
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    fullWidth = false,
    leftIcon,
    rightIcon,
    hapticStyle = "light",
    className,
    disabled,
    children,
    onClick,
    ...rest
  },
  ref,
) {
  return (
    <motion.button
      ref={ref}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
      onClick={(e) => {
        if (!disabled && !loading) {
          haptic.impact(hapticStyle);
          onClick?.(e);
        }
      }}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-2 font-semibold whitespace-nowrap",
        "transition-[box-shadow,background,color] duration-200",
        "disabled:opacity-50 disabled:pointer-events-none",
        "select-none",
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && "w-full",
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Loader2 className="size-4 animate-spin" />
      ) : leftIcon ? (
        <span className="-ml-0.5 [&_svg]:size-4">{leftIcon}</span>
      ) : null}
      <span>{children as React.ReactNode}</span>
      {!loading && rightIcon ? <span className="-mr-0.5 [&_svg]:size-4">{rightIcon}</span> : null}
    </motion.button>
  );
});
